/**
 * F-040 / REQ-032-002. A small, self-contained QR Code encoder — no npm dependency.
 *
 * `package.json` had no QR library before this card (verified by reading it), and the brief asks
 * to avoid adding one if it can be avoided. This module implements the ISO/IEC 18004 encoding
 * pipeline (byte mode only — the `otpauth://` URI this renders always contains lowercase letters
 * and `?`/`=`/`&`, none of which fit the QR "alphanumeric" mode's restricted character set, so byte
 * mode is the only mode that ever applies here and the alphanumeric/numeric modes are not
 * implemented at all) well enough to encode arbitrary UTF-8 text into a scannable matrix:
 * Reed-Solomon error correction (level M), automatic version selection, standard mask-pattern
 * selection via the four ISO penalty rules, and the finder/timing/alignment/format/version
 * function patterns.
 *
 * The Reed-Solomon block table (`RS_BLOCK_TABLE`) and alignment-pattern position table
 * (`PATTERN_POSITION_TABLE`) below are the fixed values every conformant QR encoder must use
 * (ISO/IEC 18004 Annex/Table 9); they cannot be derived from anything else, so they are quoted
 * verbatim. Correctness was verified during development by cross-checking this module's generated
 * matrices, module-for-module, against the widely used `qrcode` Python package for fixed mask
 * patterns 0–7 across several inputs (including a real `otpauth://` URI) — not just against this
 * module's own tests.
 */

// ---------------------------------------------------------------------------
// GF(256) arithmetic (Reed-Solomon)
// ---------------------------------------------------------------------------

const EXP_TABLE: number[] = new Array(256)
const LOG_TABLE: number[] = new Array(256)

for (let i = 0; i < 8; i++) EXP_TABLE[i] = 1 << i
for (let i = 8; i < 256; i++) {
  EXP_TABLE[i] = EXP_TABLE[i - 4] ^ EXP_TABLE[i - 5] ^ EXP_TABLE[i - 6] ^ EXP_TABLE[i - 8]
}
for (let i = 0; i < 255; i++) LOG_TABLE[EXP_TABLE[i]] = i

function gexp(n: number): number {
  let m = n % 255
  if (m < 0) m += 255
  return EXP_TABLE[m]
}
function glog(n: number): number {
  if (n < 1) throw new Error(`glog(${n})`)
  return LOG_TABLE[n]
}

/** Polynomial over GF(256), represented as coefficients from highest to lowest degree. */
class Poly {
  readonly num: number[]

  constructor(num: number[], shift: number) {
    let offset = 0
    while (offset < num.length - 1 && num[offset] === 0) offset++
    this.num = num.slice(offset).concat(new Array(shift).fill(0))
  }

  get(i: number): number {
    return this.num[i] ?? 0
  }

  get length(): number {
    return this.num.length
  }

  multiply(other: Poly): Poly {
    const result = new Array(this.length + other.length - 1).fill(0)
    for (let i = 0; i < this.length; i++) {
      for (let j = 0; j < other.length; j++) {
        result[i + j] ^= gexp(glog(this.get(i)) + glog(other.get(j)))
      }
    }
    return new Poly(result, 0)
  }

  mod(other: Poly): Poly {
    const difference = this.length - other.length
    if (difference < 0) return this
    const ratio = glog(this.get(0)) - glog(other.get(0))
    // Only the first `other.length` coefficients are transformed against `other` (mirrors
    // Python's `zip(self, other)`, which truncates to the shorter sequence); the remaining
    // `difference` trailing coefficients of `this` are carried over unchanged. Transforming ALL
    // of `this.num` here (an earlier version of this file did, using `this.num.map`) would call
    // `glog(0)` on the implicit zero padding past `other.length` and throw.
    const num: number[] = []
    for (let i = 0; i < other.length; i++) {
      num.push(this.get(i) ^ gexp(glog(other.get(i)) + ratio))
    }
    if (difference > 0) num.push(...this.num.slice(this.num.length - difference))
    return new Poly(num, 0).mod(other)
  }
}

// ---------------------------------------------------------------------------
// Fixed tables (ISO/IEC 18004) — quoted, not derived; see this file's remarks.
// ---------------------------------------------------------------------------

/**
 * One row per QR version (1–40), error-correction level M only (this module never uses L/Q/H):
 * `[blockCount1, totalCount1, dataCount1]` or, when the version's data is split into two groups of
 * differently sized blocks, `[blockCount1, totalCount1, dataCount1, blockCount2, totalCount2, dataCount2]`.
 */
const RS_BLOCK_TABLE_M: readonly (readonly number[])[] = [
  [1, 26, 16], [1, 44, 28], [1, 70, 44], [2, 50, 32], [2, 67, 43],
  [4, 43, 27], [4, 49, 31], [2, 60, 38, 2, 61, 39], [3, 58, 36, 2, 59, 37], [4, 69, 43, 1, 70, 44],
  [1, 80, 50, 4, 81, 51], [6, 58, 36, 2, 59, 37], [8, 59, 37, 1, 60, 38], [4, 64, 40, 5, 65, 41], [5, 65, 41, 5, 66, 42],
  [7, 73, 45, 3, 74, 46], [10, 74, 46, 1, 75, 47], [9, 69, 43, 4, 70, 44], [3, 70, 44, 11, 71, 45], [3, 67, 41, 13, 68, 42],
  [17, 68, 42], [17, 74, 46], [4, 75, 47, 14, 76, 48], [6, 73, 45, 14, 74, 46], [8, 75, 47, 13, 76, 48],
  [19, 74, 46, 4, 75, 47], [22, 73, 45, 3, 74, 46], [3, 73, 45, 23, 74, 46], [21, 73, 45, 7, 74, 46], [19, 75, 47, 10, 76, 48],
  [2, 74, 46, 29, 75, 47], [10, 74, 46, 23, 75, 47], [14, 74, 46, 21, 75, 47], [14, 74, 46, 23, 75, 47], [12, 75, 47, 26, 76, 48],
  [6, 75, 47, 34, 76, 48], [29, 74, 46, 14, 75, 47], [13, 74, 46, 32, 75, 47], [40, 75, 47, 7, 76, 48], [18, 75, 47, 31, 76, 48],
]

/** Alignment-pattern center coordinates, one row per version (1–40); empty for version 1. */
const PATTERN_POSITION_TABLE: readonly (readonly number[])[] = [
  [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
  [6, 30, 54], [6, 32, 58], [6, 34, 62], [6, 26, 46, 66], [6, 26, 48, 70], [6, 26, 50, 74], [6, 30, 54, 78], [6, 30, 56, 82], [6, 30, 58, 86], [6, 34, 62, 90],
  [6, 28, 50, 72, 94], [6, 26, 50, 74, 98], [6, 30, 54, 78, 102], [6, 28, 54, 80, 106], [6, 32, 58, 84, 110], [6, 30, 58, 86, 114], [6, 34, 62, 90, 118], [6, 26, 50, 74, 98, 122], [6, 30, 54, 78, 102, 126], [6, 26, 52, 78, 104, 130],
  [6, 30, 56, 82, 108, 134], [6, 34, 60, 86, 112, 138], [6, 30, 58, 86, 114, 142], [6, 34, 62, 90, 118, 146], [6, 30, 54, 78, 102, 126, 150], [6, 24, 50, 76, 102, 128, 154], [6, 28, 54, 80, 106, 132, 158], [6, 32, 58, 84, 110, 136, 162], [6, 26, 54, 82, 110, 138, 166], [6, 30, 58, 86, 114, 142, 170],
]

/** Format-info generator polynomial (`0b10100110111`) and XOR mask, ISO/IEC 18004 Annex C. */
const G15 = 0b10100110111
const G15_MASK = 0b101010000010010
/** Version-info generator polynomial (`0b1111100100101`), ISO/IEC 18004 Annex D — versions ≥ 7 only. */
const G18 = 0b1111100100101
/** Format-info's 2-bit error-correction-level indicator for level M (L=01, M=00, Q=11, H=10). */
const ECC_M_FORMAT_BITS = 0b00

function bchDigit(data: number): number {
  let digit = 0
  let d = data
  while (d !== 0) {
    digit++
    d = Math.floor(d / 2)
  }
  return digit
}

function bchTypeInfo(data: number): number {
  let d = data << 10
  while (bchDigit(d) - bchDigit(G15) >= 0) {
    d ^= G15 << (bchDigit(d) - bchDigit(G15))
  }
  return ((data << 10) | d) ^ G15_MASK
}

function bchTypeNumber(data: number): number {
  let d = data << 12
  while (bchDigit(d) - bchDigit(G18) >= 0) {
    d ^= G18 << (bchDigit(d) - bchDigit(G18))
  }
  return (data << 12) | d
}

// ---------------------------------------------------------------------------
// Bit buffer
// ---------------------------------------------------------------------------

class BitBuffer {
  private bytes: number[] = []
  private bitLength = 0

  get length(): number {
    return this.bitLength
  }

  putBit(bit: boolean): void {
    const byteIndex = Math.floor(this.bitLength / 8)
    if (this.bytes.length <= byteIndex) this.bytes.push(0)
    if (bit) this.bytes[byteIndex] |= 0x80 >> this.bitLength % 8
    this.bitLength++
  }

  put(num: number, length: number): void {
    for (let i = 0; i < length; i++) {
      this.putBit(((num >> (length - i - 1)) & 1) === 1)
    }
  }

  toBytes(): number[] {
    return this.bytes
  }
}

// ---------------------------------------------------------------------------
// Version / data-block selection (byte mode only)
// ---------------------------------------------------------------------------

interface RsBlock {
  totalCount: number
  dataCount: number
}

function rsBlocksForVersion(version: number): RsBlock[] {
  const row = RS_BLOCK_TABLE_M[version - 1]
  const blocks: RsBlock[] = []
  for (let i = 0; i < row.length; i += 3) {
    const [count, totalCount, dataCount] = [row[i], row[i + 1], row[i + 2]]
    for (let k = 0; k < count; k++) blocks.push({ totalCount, dataCount })
  }
  return blocks
}

/** Byte-mode character-count indicator length in bits: 8 for versions 1–9, 16 for versions 10–40. */
function charCountBits(version: number): number {
  return version < 10 ? 8 : 16
}

/** Smallest version (error-correction level M) whose data capacity fits `byteLength` bytes of byte-mode payload, or throws. */
function pickVersion(byteLength: number): number {
  for (let version = 1; version <= 40; version++) {
    const bitLimit = rsBlocksForVersion(version).reduce((sum, b) => sum + b.dataCount * 8, 0)
    const neededBits = 4 + charCountBits(version) + byteLength * 8
    if (neededBits <= bitLimit) return version
  }
  throw new Error('Testo troppo lungo per un QR code.')
}

const PAD0 = 0xec
const PAD1 = 0x11

/** Builds the padded, terminated bit buffer for `data` (byte mode) at the given version. */
function encodeByteModeBuffer(data: Uint8Array, version: number, bitLimit: number): BitBuffer {
  const buffer = new BitBuffer()
  buffer.put(0b0100, 4) // byte-mode indicator
  buffer.put(data.length, charCountBits(version))
  for (const byte of data) buffer.put(byte, 8)

  if (buffer.length > bitLimit) {
    throw new Error('Testo troppo lungo per un QR code.')
  }

  for (let i = 0; i < Math.min(bitLimit - buffer.length, 4); i++) buffer.putBit(false)

  const delimit = buffer.length % 8
  if (delimit) for (let i = 0; i < 8 - delimit; i++) buffer.putBit(false)

  const bytesToFill = Math.floor((bitLimit - buffer.length) / 8)
  for (let i = 0; i < bytesToFill; i++) buffer.put(i % 2 === 0 ? PAD0 : PAD1, 8)

  return buffer
}

/** Reed-Solomon-encodes and interleaves `buffer`'s bytes across `rsBlocks`, per ISO/IEC 18004 §8.5/8.6. */
function createCodewords(buffer: BitBuffer, rsBlocks: RsBlock[]): number[] {
  const bytes = buffer.toBytes()
  let offset = 0
  let maxDc = 0
  let maxEc = 0
  const dcData: number[][] = []
  const ecData: number[][] = []

  for (const block of rsBlocks) {
    const dcCount = block.dataCount
    const ecCount = block.totalCount - dcCount
    maxDc = Math.max(maxDc, dcCount)
    maxEc = Math.max(maxEc, ecCount)

    const currentDc = bytes.slice(offset, offset + dcCount)
    offset += dcCount

    let rsPoly = new Poly([1], 0)
    for (let i = 0; i < ecCount; i++) rsPoly = rsPoly.multiply(new Poly([1, gexp(i)], 0))

    const rawPoly = new Poly(currentDc, rsPoly.length - 1)
    const modPoly = rawPoly.mod(rsPoly)

    const currentEc: number[] = []
    const modOffset = modPoly.length - ecCount
    for (let i = 0; i < ecCount; i++) {
      const modIndex = i + modOffset
      currentEc.push(modIndex >= 0 ? modPoly.get(modIndex) : 0)
    }

    dcData.push(currentDc)
    ecData.push(currentEc)
  }

  const result: number[] = []
  for (let i = 0; i < maxDc; i++) {
    for (const dc of dcData) if (i < dc.length) result.push(dc[i])
  }
  for (let i = 0; i < maxEc; i++) {
    for (const ec of ecData) if (i < ec.length) result.push(ec[i])
  }
  return result
}

// ---------------------------------------------------------------------------
// Matrix construction
// ---------------------------------------------------------------------------

type Cell = boolean | null
type Grid = Cell[][]

function makeBlankGrid(size: number): Grid {
  return Array.from({ length: size }, () => new Array<Cell>(size).fill(null))
}

function setupFinderPattern(grid: Grid, size: number, row: number, col: number): void {
  for (let r = -1; r <= 7; r++) {
    if (row + r <= -1 || size <= row + r) continue
    for (let c = -1; c <= 7; c++) {
      if (col + c <= -1 || size <= col + c) continue
      const dark = (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
        (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
        (r >= 2 && r <= 4 && c >= 2 && c <= 4)
      grid[row + r][col + c] = dark
    }
  }
}

function setupTimingPattern(grid: Grid, size: number): void {
  for (let r = 8; r < size - 8; r++) {
    if (grid[r][6] !== null) continue
    grid[r][6] = r % 2 === 0
  }
  for (let c = 8; c < size - 8; c++) {
    if (grid[6][c] !== null) continue
    grid[6][c] = c % 2 === 0
  }
}

function setupAlignmentPatterns(grid: Grid, version: number): void {
  const positions = PATTERN_POSITION_TABLE[version - 1]
  for (const row of positions) {
    for (const col of positions) {
      if (grid[row][col] !== null) continue
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          const dark = r === -2 || r === 2 || c === -2 || c === 2 || (r === 0 && c === 0)
          grid[row + r][col + c] = dark
        }
      }
    }
  }
}

function setupVersionInfo(grid: Grid, size: number, version: number, test: boolean): void {
  if (version < 7) return
  const bits = bchTypeNumber(version)
  for (let i = 0; i < 18; i++) {
    const mod = !test && ((bits >> i) & 1) === 1
    grid[Math.floor(i / 3)][(i % 3) + size - 8 - 3] = mod
    grid[(i % 3) + size - 8 - 3][Math.floor(i / 3)] = mod
  }
}

function setupFormatInfo(grid: Grid, size: number, maskPattern: number, test: boolean): void {
  const data = (ECC_M_FORMAT_BITS << 3) | maskPattern
  const bits = bchTypeInfo(data)

  for (let i = 0; i < 15; i++) {
    const mod = !test && ((bits >> i) & 1) === 1
    if (i < 6) grid[i][8] = mod
    else if (i < 8) grid[i + 1][8] = mod
    else grid[size - 15 + i][8] = mod
  }
  for (let i = 0; i < 15; i++) {
    const mod = !test && ((bits >> i) & 1) === 1
    if (i < 8) grid[8][size - i - 1] = mod
    else if (i < 9) grid[8][15 - i - 1 + 1] = mod
    else grid[8][15 - i - 1] = mod
  }
  grid[size - 8][8] = !test
}

function maskFunc(pattern: number): (r: number, c: number) => boolean {
  switch (pattern) {
    case 0: return (r, c) => (r + c) % 2 === 0
    case 1: return (r) => r % 2 === 0
    case 2: return (_r, c) => c % 3 === 0
    case 3: return (r, c) => (r + c) % 3 === 0
    case 4: return (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0
    case 5: return (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0
    case 6: return (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0
    case 7: return (r, c) => (((r * c) % 3) + ((r + c) % 2)) % 2 === 0
    default: throw new Error(`Mask pattern non valido: ${pattern}`)
  }
}

function mapData(grid: Grid, size: number, data: number[], maskPattern: number): void {
  let row = size - 1
  let inc = -1
  let bitIndex = 7
  let byteIndex = 0
  const mask = maskFunc(maskPattern)

  // NOTE: the outer loop's step must be computed from its own original sequence (size-1, size-3,
  // ...), independent of the `col <= 6` adjustment below — mirroring Python's `for col in
  // range(size-1, 0, -2)`, where reassigning `col` inside the loop body does not affect the next
  // value `range()` yields. A single mutable `col` used both as the loop counter and the adjusted
  // value (as a naive line-for-line port of the Python would do) silently drifts the column
  // sequence in JS's C-style `for`, since `col -= 2` in the loop header re-reads the
  // already-decremented value — verified against the Python reference's actual column sequence
  // during this module's development.
  for (let colBase = size - 1; colBase > 0; colBase -= 2) {
    let col = colBase
    if (col <= 6) col--
    for (;;) {
      for (const c of [col, col - 1]) {
        if (grid[row][c] === null) {
          let dark = false
          if (byteIndex < data.length) dark = ((data[byteIndex] >> bitIndex) & 1) === 1
          if (mask(row, c)) dark = !dark
          grid[row][c] = dark
          bitIndex--
          if (bitIndex === -1) {
            byteIndex++
            bitIndex = 7
          }
        }
      }
      row += inc
      if (row < 0 || size <= row) {
        row -= inc
        inc = -inc
        break
      }
    }
  }
}

function buildGrid(version: number, size: number, codewords: number[] | null, maskPattern: number, test: boolean): Grid {
  const grid = makeBlankGrid(size)
  setupFinderPattern(grid, size, 0, 0)
  setupFinderPattern(grid, size, size - 7, 0)
  setupFinderPattern(grid, size, 0, size - 7)
  setupAlignmentPatterns(grid, version)
  setupTimingPattern(grid, size)
  setupFormatInfo(grid, size, maskPattern, test)
  setupVersionInfo(grid, size, version, test)
  if (codewords) mapData(grid, size, codewords, maskPattern)
  return grid
}

// ---------------------------------------------------------------------------
// Mask-pattern penalty scoring (ISO/IEC 18004 §8.8.2)
// ---------------------------------------------------------------------------

function lostPointLevel1(grid: Grid, size: number): number {
  let lost = 0
  const container = new Array(size + 1).fill(0)

  for (let row = 0; row < size; row++) {
    let previous = grid[row][0]
    let length = 0
    for (let col = 0; col < size; col++) {
      if (grid[row][col] === previous) {
        length++
      } else {
        if (length >= 5) container[length]++
        length = 1
        previous = grid[row][col]
      }
    }
    if (length >= 5) container[length]++
  }

  for (let col = 0; col < size; col++) {
    let previous = grid[0][col]
    let length = 0
    for (let row = 0; row < size; row++) {
      if (grid[row][col] === previous) {
        length++
      } else {
        if (length >= 5) container[length]++
        length = 1
        previous = grid[row][col]
      }
    }
    if (length >= 5) container[length]++
  }

  for (let len = 5; len <= size; len++) lost += container[len] * (len - 2)
  return lost
}

function lostPointLevel2(grid: Grid, size: number): number {
  let lost = 0
  for (let row = 0; row < size - 1; row++) {
    for (let col = 0; col < size - 1; col++) {
      const a = grid[row][col]
      const b = grid[row][col + 1]
      const c = grid[row + 1][col]
      const d = grid[row + 1][col + 1]
      if (a === b && a === c && a === d) lost += 3
    }
  }
  return lost
}

function matchesFinderRatio(get: (offset: number) => Cell): boolean {
  // `!!(...)`: each `get(x)` is `Cell` (`boolean | null`), and by the time this runs every cell
  // is a real boolean (see `bestMaskPattern`'s remarks) — but TS still infers the raw `&&`/`||`
  // expression's type as `boolean | null` from `Cell`'s declared type, which `tsc -b`'s stricter
  // project-reference check (unlike a plain `tsc --noEmit`) rejects against this function's
  // declared `boolean` return type.
  return !!(
    !get(1) && get(4) && !get(5) && get(6) && !get(9) &&
    ((get(0) && get(2) && get(3) && !get(7) && !get(8) && !get(10)) ||
      (!get(0) && !get(2) && !get(3) && get(7) && get(8) && get(10)))
  )
}

function lostPointLevel3(grid: Grid, size: number): number {
  let lost = 0
  for (let row = 0; row < size; row++) {
    for (let col = 0; col <= size - 11; col++) {
      if (matchesFinderRatio((o) => grid[row][col + o])) lost += 40
    }
  }
  for (let col = 0; col < size; col++) {
    for (let row = 0; row <= size - 11; row++) {
      if (matchesFinderRatio((o) => grid[row + o][col])) lost += 40
    }
  }
  return lost
}

function lostPointLevel4(grid: Grid, size: number): number {
  let dark = 0
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) if (grid[row][col]) dark++
  }
  const percent = (dark / (size * size)) * 100
  return Math.floor(Math.abs(percent - 50) / 5) * 10
}

function lostPoint(grid: Grid, size: number): number {
  return (
    lostPointLevel1(grid, size) +
    lostPointLevel2(grid, size) +
    lostPointLevel3(grid, size) +
    lostPointLevel4(grid, size)
  )
}

function bestMaskPattern(version: number, size: number, codewords: number[]): number {
  let best = 0
  let bestLost = Infinity
  for (let pattern = 0; pattern < 8; pattern++) {
    const grid = buildGrid(version, size, codewords, pattern, true)
    const lost = lostPoint(grid, size)
    if (lost < bestLost) {
      bestLost = lost
      best = pattern
    }
  }
  return best
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface QrMatrix {
  /** Module count per side, quiet zone excluded. */
  size: number
  /** `matrix[row][col] === true` means a dark (filled) module. */
  matrix: boolean[][]
}

/** Encodes `text` as a QR code (byte mode, error-correction level M). Throws if it does not fit within version 40. */
export function encodeQr(text: string): QrMatrix {
  const data = new TextEncoder().encode(text)
  const version = pickVersion(data.length)
  const rsBlocks = rsBlocksForVersion(version)
  const bitLimit = rsBlocks.reduce((sum, b) => sum + b.dataCount * 8, 0)
  const buffer = encodeByteModeBuffer(data, version, bitLimit)
  const codewords = createCodewords(buffer, rsBlocks)

  const size = version * 4 + 17
  const maskPattern = bestMaskPattern(version, size, codewords)
  const grid = buildGrid(version, size, codewords, maskPattern, false)

  const matrix = grid.map((row) => row.map((cell) => cell === true))
  return { size, matrix }
}
